import {Component, Input, OnInit, computed, signal} from '@angular/core';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {
  CUSTOM_PERM_SECTIONS,
  allChildRuleIds,
  ICustomPermNode,
  ICustomPermSection,
  isIndeterminate,
  isViewSearchLocked,
  syncParentSelections,
  syncStockViewSearchRequirement,
  toggleRuleSelection,
} from '../../utils/custom-permissions.utils';
import {RuleHumanIdEnum, RuleHumanID} from '../../models/rule-info.enum';

export interface ICustomisePermissionsModalResult {
  confirmed: boolean;
  selectedRuleIds?: RuleHumanID[];
}

@Component({
  selector: 'app-customise-permissions-modal',
  templateUrl: './customise-permissions-modal.component.html',
  styleUrls: ['./customise-permissions-modal.component.scss'],
  standalone: false,
})
export class CustomisePermissionsModalComponent implements OnInit {
  @Input() contextLabel = '';
  @Input() initialSelected: RuleHumanID[] = [];

  protected readonly sections: ICustomPermSection[] = CUSTOM_PERM_SECTIONS;
  protected readonly selected = signal<Set<RuleHumanID>>(new Set());
  protected readonly initialSelection = signal<Set<RuleHumanID>>(new Set());
  protected readonly hasChanges = computed((): boolean => {
    const current = this.selected();
    const initial = this.initialSelection();
    if (current.size !== initial.size) {
      return true;
    }
    for (const id of current) {
      if (!initial.has(id)) {
        return true;
      }
    }
    return false;
  });
  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly staffAccessRuleEnum = RuleHumanIdEnum;

  constructor(private readonly modalController: ModalControllerService) {}

  protected get selectedCount(): number {
    return this.selected().size;
  }

  ngOnInit(): void {
    const initial = syncStockViewSearchRequirement(syncParentSelections(new Set(this.initialSelected)));
    this.selected.set(initial);
    this.initialSelection.set(new Set(initial));
  }

  protected isChecked(node: ICustomPermNode): boolean {
    return this.selected().has(node.key);
  }

  protected allChildrenChecked(node: ICustomPermNode): boolean {
    const childKeys = allChildRuleIds(node);
    if (!childKeys.length) {
      return this.isChecked(node);
    }
    return childKeys.every((key: RuleHumanID) => this.selected().has(key));
  }

  protected isNodeIndeterminate(node: ICustomPermNode): boolean {
    return isIndeterminate(node, this.selected());
  }

  protected hasChildren(node: ICustomPermNode): boolean {
    return !!node.children?.length;
  }

  protected isAdminRule(node: ICustomPermNode): boolean {
    return node.key === RuleHumanIdEnum['a.'];
  }

  protected isCheckboxDisabled(node: ICustomPermNode): boolean {
    return node.key === RuleHumanIdEnum['b.'] && isViewSearchLocked(this.selected());
  }

  protected onToggle(node: ICustomPermNode, checked: boolean): void {
    this.selected.set(toggleRuleSelection(node, checked, this.selected()));
  }

  protected onCheckboxChange(node: ICustomPermNode, event: Event): void {
    const input = event.target as HTMLInputElement;
    const childKeys = allChildRuleIds(node);

    if (childKeys.length > 0) {
      const shouldCheck = this.isNodeIndeterminate(node) ? true : input.checked;
      this.onToggle(node, shouldCheck);
      return;
    }

    this.onToggle(node, input.checked);
  }

  protected async onCancel(): Promise<void> {
    await this.modalController.dismiss({confirmed: false} satisfies ICustomisePermissionsModalResult);
  }

  protected async onSave(): Promise<void> {
    await this.modalController.dismiss({
      confirmed: true,
      selectedRuleIds: Array.from(this.selected()),
    } satisfies ICustomisePermissionsModalResult);
  }

  protected trackSection(_index: number, section: ICustomPermSection): string {
    return section.category ?? 'standalone';
  }

  protected trackNode(_index: number, node: ICustomPermNode): string {
    return node.key;
  }
}
